import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import type { AvatarAccent } from '@zealot/ui/lib/getAvatarAccent';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

export type { AvatarAccent };

// gloss
const AVATAR_ACCENT_CLASSES: Record<AvatarAccent, string> = {
  1: 'bg-avatar-1/15 text-avatar-1',
  2: 'bg-avatar-2/15 text-avatar-2',
  3: 'bg-avatar-3/15 text-avatar-3',
  4: 'bg-avatar-4/15 text-avatar-4',
  5: 'bg-avatar-5/15 text-avatar-5',
  6: 'bg-avatar-6/15 text-avatar-6',
};

const avatarVariants = cva('group/avatar relative flex shrink-0 rounded-full select-none', {
  variants: {
    size: {
      xs: 'size-6',
      sm: 'size-8',
      default: 'size-10',
      lg: 'size-12',
      xl: 'size-16',
    },
  },
  defaultVariants: { size: 'default' },
});

type AvatarProps = AvatarPrimitive.Root.Props & VariantProps<typeof avatarVariants>;

const Avatar = ({ className, size = 'default', ...props }: AvatarProps) => {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(avatarVariants({ size, className }))}
      {...props}
    />
  );
};

// gloss
const AvatarImage = ({ className, ...props }: AvatarPrimitive.Image.Props) => {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('relative aspect-square size-full rounded-[inherit] object-cover', className)}
      {...props}
    />
  );
};

// gloss
type AvatarFallbackProps = AvatarPrimitive.Fallback.Props & {
  accent?: AvatarAccent;
};

// gloss
const AvatarFallback = ({ className, accent, ...props }: AvatarFallbackProps) => {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'absolute inset-0 flex items-center justify-center rounded-[inherit] text-sm',
        accent ? AVATAR_ACCENT_CLASSES[accent] : 'bg-muted text-muted-foreground',
        'group-data-[size=xs]/avatar:text-[0.625rem]',
        'group-data-[size=sm]/avatar:text-xs',
        'group-data-[size=lg]/avatar:text-base',
        'group-data-[size=xl]/avatar:text-xl',
        "[&_svg:not([class*='size-'])]:size-5 [&_svg]:shrink-0",
        "group-data-[size=xs]/avatar:[&_svg:not([class*='size-'])]:size-3",
        "group-data-[size=sm]/avatar:[&_svg:not([class*='size-'])]:size-4",
        "group-data-[size=lg]/avatar:[&_svg:not([class*='size-'])]:size-6",
        "group-data-[size=xl]/avatar:[&_svg:not([class*='size-'])]:size-8",
        className,
      )}
      {...props}
    />
  );
};

// gloss
const avatarBadgeVariants = cva('absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full', {
  variants: {
    variant: {
      dot: cn(
        'bg-primary text-primary-foreground ring-2 ring-background',
        'group-data-[size=xs]/avatar:size-2 group-data-[size=xs]/avatar:[&>svg]:hidden',
        'group-data-[size=sm]/avatar:size-2.5 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-3 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3.5 group-data-[size=lg]/avatar:[&>svg]:size-2.5',
        'group-data-[size=xl]/avatar:size-4 group-data-[size=xl]/avatar:[&>svg]:size-3',
      ),
      image: cn(
        'overflow-hidden bg-background ring-2 ring-background [&>img]:size-full [&>img]:rounded-full [&>img]:object-cover',
        'group-data-[size=xs]/avatar:size-3',
        'group-data-[size=sm]/avatar:size-3.5',
        'group-data-[size=default]/avatar:size-4',
        'group-data-[size=lg]/avatar:size-5',
        'group-data-[size=xl]/avatar:size-6',
      ),
    },
  },
  defaultVariants: { variant: 'dot' },
});

type AvatarBadgeProps = React.ComponentProps<'span'> & VariantProps<typeof avatarBadgeVariants>;

const AvatarBadge = ({ className, variant = 'dot', ...props }: AvatarBadgeProps) => {
  return (
    <span
      data-slot="avatar-badge"
      data-variant={variant}
      className={cn(avatarBadgeVariants({ variant, className }))}
      {...props}
    />
  );
};

const AvatarGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        'group/avatar-group flex -space-x-2.5 [&>[data-slot=avatar]]:ring-2 [&>[data-slot=avatar]]:ring-background',
        className,
      )}
      {...props}
    />
  );
};

const AvatarGroupCount = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        'relative flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-sm ring-2 ring-background',
        className,
      )}
      {...props}
    />
  );
};

export {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
  avatarBadgeVariants,
  avatarVariants,
};
